/**
 * WebOs - Login Screen & First Boot Assistant
 *
 * Gère l'authentification utilisateur et la création du premier compte.
 */

import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { createLogger } from '../utils/debug.js';
import { getAuth } from '../kernel/auth.js';

const log = createLogger('Login');

// Thème Catppuccin Mocha
const CATPPUCCIN_THEME = {
  background: '#1e1e2e',
  foreground: '#cdd6f4',
  cursor: '#f5e0dc',
  cursorAccent: '#1e1e2e',
  selectionBackground: '#585b70',
  selectionForeground: '#cdd6f4',
  black: '#45475a',
  red: '#f38ba8',
  green: '#a6e3a1',
  yellow: '#f9e2af',
  blue: '#89b4fa',
  magenta: '#cba6f7',
  cyan: '#94e2d5',
  white: '#bac2de',
  brightBlack: '#585b70',
  brightRed: '#f38ba8',
  brightGreen: '#a6e3a1',
  brightYellow: '#f9e2af',
  brightBlue: '#89b4fa',
  brightMagenta: '#cba6f7',
  brightCyan: '#94e2d5',
  brightWhite: '#a6adc8',
};

// Couleurs ANSI
const COLORS = {
  reset: '\x1b[0m',
  bold: '\x1b[1m',
  dim: '\x1b[2m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  red: '\x1b[31m',
  bgBlue: '\x1b[44m',
  bgMagenta: '\x1b[45m',
};

/**
 * Classe Login - Écran de connexion
 */
export class Login {
  constructor(container, fs) {
    this.container = container;
    this.fs = fs;
    this.auth = getAuth();
    this.terminal = null;
    this.fitAddon = null;

    // État
    this.currentInput = '';
    this.isPasswordMode = false;
    this.inputCallback = null;

    log.debug('Login screen created');
  }

  /**
   * Initialise l'écran de login
   */
  async init() {
    log.debug('Initializing login screen...');

    // Configurer l'auth avec le fs
    this.auth.configure(this.fs);

    // Créer le terminal
    this.terminal = new Terminal({
      theme: CATPPUCCIN_THEME,
      fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
      fontSize: 14,
      lineHeight: 1.2,
      cursorBlink: true,
      cursorStyle: 'block',
      scrollback: 100,
    });

    this.fitAddon = new FitAddon();
    this.terminal.loadAddon(this.fitAddon);

    // Monter le terminal
    this.terminal.open(this.container);

    // Fit
    this.fitAddon.fit();
    setTimeout(() => this.fitAddon.fit(), 100);

    // Gérer le redimensionnement
    window.addEventListener('resize', () => this.fitAddon.fit());

    // Écouter les entrées
    this.terminal.onData(data => this.handleInput(data));

    log.info('Login screen initialized');
  }

  /**
   * Lance l'assistant de premier boot
   */
  async runFirstBootAssistant() {
    log.info('Running first boot assistant...');

    this.clear();
    this.printWelcome();

    await this.sleep(500);

    this.println('');
    this.println(`${COLORS.yellow}Bienvenue dans l'assistant de configuration !${COLORS.reset}`);
    this.println('');
    this.println('Aucun utilisateur n\'a été configuré.');
    this.println('Créons votre compte administrateur.');
    this.println('');

    await this.sleep(300);

    // Demander le nom d'utilisateur
    let username = '';
    while (!username) {
      username = await this.prompt('Nom d\'utilisateur: ');
      username = username.trim().toLowerCase();

      if (!username) {
        this.printError('Le nom d\'utilisateur ne peut pas être vide.');
        continue;
      }

      if (!/^[a-z][a-z0-9_-]{0,31}$/.test(username)) {
        this.printError('Nom invalide. Utilisez lettres minuscules, chiffres, - ou _');
        username = '';
        continue;
      }
    }

    // Demander le mot de passe
    let password = '';
    while (!password) {
      password = await this.prompt('Mot de passe: ', true);

      if (password.length < 4) {
        this.printError('Le mot de passe doit faire au moins 4 caractères.');
        password = '';
        continue;
      }

      const confirm = await this.prompt('Confirmer le mot de passe: ', true);

      if (password !== confirm) {
        this.printError('Les mots de passe ne correspondent pas.');
        password = '';
        continue;
      }
    }

    // Demander le nom d'affichage (optionnel)
    this.println('');
    const displayName = await this.prompt(`Nom complet (optionnel, Entrée pour "${username}"): `);

    this.println('');
    this.println(`${COLORS.cyan}Création du compte...${COLORS.reset}`);

    // Créer le compte
    const success = await this.auth.createFirstUser(
      username,
      password,
      displayName || username
    );

    if (success) {
      this.println(`${COLORS.green}Compte créé avec succès !${COLORS.reset}`);
      this.println('');

      await this.sleep(500);

      this.println(`${COLORS.dim}Connexion automatique...${COLORS.reset}`);
      await this.sleep(500);

      // Login automatique
      const result = await this.auth.login(username, password);
      return result;
    } else {
      this.printError('Erreur lors de la création du compte.');
      return { success: false };
    }
  }

  /**
   * Affiche l'écran de login
   */
  async runLoginScreen() {
    log.info('Running login screen...');

    this.clear();
    this.printWelcome();

    this.println('');

    let attempts = 0;
    const maxAttempts = 5;

    while (attempts < maxAttempts) {
      // Demander le nom d'utilisateur
      const username = await this.prompt('login: ');

      if (!username) {
        continue;
      }

      // Demander le mot de passe
      const password = await this.prompt('Password: ', true);

      // Tentative de connexion
      const result = await this.auth.login(username, password);

      if (result.success) {
        this.println('');
        this.println(`${COLORS.green}Connexion réussie !${COLORS.reset}`);
        await this.sleep(300);
        return result;
      }

      attempts++;
      this.println(`${COLORS.red}${result.error}${COLORS.reset}`);

      if (attempts < maxAttempts) {
        this.println(`${COLORS.dim}(${maxAttempts - attempts} tentatives restantes)${COLORS.reset}`);
      }

      this.println('');
    }

    this.printError('Trop de tentatives. Veuillez rafraîchir la page.');
    return { success: false };
  }

  /**
   * Point d'entrée principal
   */
  async run() {
    // Vérifier si c'est le premier boot
    const isFirstBoot = await this.auth.isFirstBoot();

    if (isFirstBoot) {
      return await this.runFirstBootAssistant();
    } else {
      return await this.runLoginScreen();
    }
  }

  /**
   * Affiche le message de bienvenue
   */
  printWelcome() {
    const C = COLORS;
    this.terminal.write('\r\n');
    this.terminal.write(`${C.blue}${C.bold}░██       ░██            ░██          ░██████                                              ░██                                           ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░██       ░██            ░██         ░██   ░██                                                                                           ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░██  ░██  ░██  ░███████  ░████████  ░██     ░██  ░███████            ░████████   ░███████  ░██          ░██    ░██ ░██    ░██ ░█████████ ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░██ ░████ ░██ ░██    ░██ ░██    ░██ ░██     ░██ ░██                  ░██    ░██ ░██        ░██           ░██  ░██  ░██    ░██      ░███  ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░██░██ ░██░██ ░█████████ ░██    ░██ ░██     ░██  ░███████            ░██    ░██  ░███████  ░██            ░█████   ░██    ░██    ░███    ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░████   ░████ ░██        ░███   ░██  ░██   ░██         ░██           ░██    ░██        ░██ ░██           ░██  ░██  ░██   ░███  ░███      ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}░███     ░███  ░███████  ░██░█████    ░██████    ░███████     ░██    ░██    ░██  ░███████  ░██   ░██    ░██    ░██  ░█████░██ ░█████████ ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}                                                                                                                          ░██            ${C.reset}\r\n`);
    this.terminal.write(`${C.blue}${C.bold}                                                                                                                    ░███████             ${C.reset}\r\n`);
    this.terminal.write('\r\n');
  }

  /**
   * Efface l'écran
   */
  clear() {
    this.terminal.write('\x1b[2J\x1b[H');
  }

  /**
   * Affiche du texte avec retour à la ligne
   */
  println(text) {
    this.terminal.write(text + '\r\n');
  }

  /**
   * Affiche une erreur
   */
  printError(text) {
    this.println(`${COLORS.red}Erreur: ${text}${COLORS.reset}`);
  }

  /**
   * Attend un délai
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Demande une entrée utilisateur
   */
  prompt(message, isPassword = false) {
    return new Promise(resolve => {
      this.terminal.write(message);
      this.currentInput = '';
      this.isPasswordMode = isPassword;

      this.inputCallback = (input) => {
        resolve(input);
      };
    });
  }

  /**
   * Gère les entrées clavier
   */
  handleInput(data) {
    // Si pas de callback, ignorer
    if (!this.inputCallback) return;

    const code = data.charCodeAt(0);

    // Entrée
    if (data === '\r') {
      this.terminal.write('\r\n');
      const input = this.currentInput;
      this.currentInput = '';
      this.isPasswordMode = false;

      const callback = this.inputCallback;
      this.inputCallback = null;
      callback(input);
      return;
    }

    // Backspace
    if (data === '\x7f' || data === '\b') {
      if (this.currentInput.length > 0) {
        this.currentInput = this.currentInput.slice(0, -1);
        if (!this.isPasswordMode) {
          this.terminal.write('\b \b');
        }
      }
      return;
    }

    // Ctrl+C
    if (data === '\x03') {
      this.terminal.write('^C\r\n');
      this.currentInput = '';
      const callback = this.inputCallback;
      this.inputCallback = null;
      callback('');
      return;
    }

    // Caractère imprimable
    if (code >= 32 && code < 127) {
      this.currentInput += data;
      if (this.isPasswordMode) {
        this.terminal.write('*');
      } else {
        this.terminal.write(data);
      }
    }
  }

  /**
   * Nettoie les ressources
   */
  destroy() {
    if (this.terminal) {
      this.terminal.dispose();
      this.terminal = null;
    }
    log.debug('Login screen destroyed');
  }
}

export default Login;
